import { CollectionConfig } from "@rebasepro/types";
import { IS_REAL_USER } from "./_auth_predicates";
import { HIDDEN_FROM_FORM } from "./_relation_display";
import jobsCollection from "./jobs";

/**
 * Companies — the single source of truth for any company entity.
 *
 * A company can be:
 *  - Auto-created by the scraping/enrichment pipeline (only domain + brand data)
 *  - Registered by a user through the company signup form (CRM data filled in)
 *  - Both: a scraped company later claimed by a registering user
 *
 * Users are linked through the `company_members` join table (many-to-many).
 * Jobs reference this table directly via `company_id`.
 */
const companiesCollection: CollectionConfig = {
    name: "Fichas de empresa",
    singularName: "Empresa",
    slug: "companies",
    table: "companies",
    securityRules: [
        {
            name: "public_read",
            operation: "select",
            using: "true"
        },
        {
            // Company registration creates the row, then claims it by inserting
            // the matching company_members row.
            //
            // This excluded only 'anonymous', and an unauthenticated request
            // carries 'anon' — so a tokenless POST created companies (verified,
            // HTTP 201). Both spellings are excluded now; see IS_REAL_USER.
            name: "authenticated_insert",
            operation: "insert",
            using: IS_REAL_USER,
            withCheck: IS_REAL_USER
        },
        {
            // Only your own company's profile — previously any signed-in user
            // could rewrite or delete any company.
            name: "member_update_own_company",
            operation: "update",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = companies.id)",
            withCheck: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = companies.id)"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },

        // ── Identity ─────────────────────────────────────────────
        name: {
            name: "Nombre",
            type: "string",
            validation: { required: true },
            description: "Nombre oficial de la empresa"
        },
        // The employer's public address, /empresas/<slug>. Minted by a trigger
        // from the name when the row is created and frozen from then on
        // (drizzle/0067): a rename — by the employer in "Mi empresa", or by the
        // brand enrichment — must not move a page somebody has already linked
        // to. The trigger ignores any value a caller sends, so this stays
        // read-only here too; the site derives the same value for a row that
        // predates the migration (src/lib/company.ts).
        slug: {
            name: "Dirección de su página",
            type: "string",
            description: "La página pública de la empresa es sustentalent.com/empresas/<esto>. Se genera al crear la empresa y no cambia aunque cambie el nombre, para que los enlaces ya compartidos sigan funcionando.",
            admin: { readOnly: true }
        },
        employer_key: {
            name: "Clave de empleador",
            type: "string",
            description: "La forma normalizada del nombre, con la que reconocemos que esta ficha y una empresa de «Empresas que contratan» son la misma. No es única: en este directorio hay duplicados reales.",
            admin: { readOnly: true }
        },
        domain: {
            name: "Dominio",
            type: "string",
            description: "Dominio web (p. ej. 'stripe.com'). Se usa como clave única para evitar duplicados.",
            validation: { unique: true }
        },

        // ── Brand / Enrichment (from Unbrand API) ────────────────
        tagline: {
            name: "Lema",
            type: "string"
        },
        description: {
            name: "Descripción",
            type: "string",
            admin: {
                multiline: true
            },
            description: "Descripción de la empresa"
        },
        mission: {
            name: "Misión",
            type: "string",
            admin: {
                multiline: true
            },
            description: "Misión de la empresa"
        },
        logo_url: {
            name: "URL del logo",
            type: "string",
            admin: {
                urlPreview: "image"
            },
            description: "URL de la imagen del logo"
        },
        // Everything below is written by `unbrand-service.ts` on each brand
        // refresh and overwritten wholesale on the next one. A hand-edit here
        // looks like it stuck and then silently disappears, so the admin shows
        // these as evidence rather than as inputs. Re-run the enrichment to
        // change them.
        logo_on_dark: {
            name: "Logo sobre fondo oscuro",
            type: "boolean",
            admin: { readOnly: true },
            description:
                "Medido al enriquecer la marca: el logo es blanco sobre transparente y " +
                "necesita fondo oscuro para verse. Vacío = no se pudo medir (el logo no " +
                "responde), y entonces la web decide por el nombre del archivo."
        },
        logo_svg: {
            name: "Logo SVG",
            type: "string",
            admin: {
                multiline: true,
                readOnly: true
            },
            description: "SVG original del logo"
        },
        primary_color: {
            name: "Color primario",
            type: "string",
            description: "Color principal de marca en hex",
            admin: { readOnly: true }
        },
        background_color: {
            name: "Color de fondo",
            type: "string",
            admin: { readOnly: true }
        },
        text_color: {
            name: "Color de texto",
            type: "string",
            admin: { readOnly: true }
        },
        colors: {
            name: "Colores",
            type: "array",
            of: {
                name: "Color",
                type: "map",
                properties: {
                    hex: { name: "Código de color", type: "string" },
                    name: { name: "Nombre", type: "string" },
                    usage: { name: "Para qué se usa", type: "string" }
                }
            },
            description: "Paleta completa de color de marca",
            admin: { readOnly: true }
        },
        fonts: {
            name: "Tipografías",
            type: "array",
            of: {
                name: "Tipografía",
                type: "map",
                properties: {
                    name: { name: "Nombre de la letra", type: "string" },
                    category: { name: "Tipo de letra", type: "string" },
                    usage: { name: "Para qué se usa", type: "string" }
                }
            },
            admin: { readOnly: true }
        },
        popularity_score: {
            name: "Popularidad",
            type: "number",
            description: "Puntuación de popularidad de Unbrand (0-10)",
            admin: { readOnly: true }
        },
        brand_fetched_at: {
            name: "Marca obtenida el",
            type: "date",
            description: "Última vez que se obtuvieron los datos de marca de Unbrand",
            admin: { readOnly: true }
        },
        raw_brand_data: {
            name: "Datos de marca (sin procesar)",
            type: "map",
            description: "Respuesta completa sin procesar de la API de Unbrand",
            properties: {},
            admin: { readOnly: true }
        },

        // ── CRM / Registration ───────────────────────────────────
        industry: {
            name: "Sector",
            type: "string",
            description: "Sector: Energía, Consultoría, Finanzas…"
        },
        size: {
            name: "Tamaño",
            type: "string",
            enum: [
                { id: "1-10", label: "1-10", color: "gray" },
                { id: "11-50", label: "11-50", color: "blue" },
                { id: "51-200", label: "51-200", color: "teal" },
                { id: "201-1000", label: "201-1000", color: "green" },
                { id: "1001+", label: "1001+", color: "purple" }
            ]
        },
        location: {
            name: "Ubicación",
            type: "string",
            description: "Ubicación de la sede"
        },
        city: {
            name: "Ciudad",
            type: "string",
            description: "Ciudad de la sede"
        },
        country: {
            name: "País",
            type: "string",
            description: "País de la sede",
            enum: [
                { id: "España", label: "España" },
                { id: "México", label: "México" },
                { id: "Colombia", label: "Colombia" },
                { id: "Chile", label: "Chile" },
                { id: "Argentina", label: "Argentina" },
                { id: "Perú", label: "Perú" },
                { id: "Uruguay", label: "Uruguay" },
                { id: "Bolivia", label: "Bolivia" },
                { id: "Paraguay", label: "Paraguay" },
                { id: "Costa Rica", label: "Costa Rica" },
                { id: "Guatemala", label: "Guatemala" },
                { id: "República Dominicana", label: "República Dominicana" },
                { id: "Panamá", label: "Panamá" },
                { id: "Ecuador", label: "Ecuador" },
                { id: "El Salvador", label: "El Salvador" },
                { id: "Honduras", label: "Honduras" },
                { id: "Nicaragua", label: "Nicaragua" },
                { id: "Venezuela", label: "Venezuela" },
                { id: "Puerto Rico", label: "Puerto Rico" },
                { id: "Estados Unidos", label: "Estados Unidos" },
                { id: "Otro", label: "Otro" }
            ]
        },
        website: {
            name: "Web",
            type: "string",
            description: "URL completa del sitio web"
        },
        linkedin_url: {
            name: "LinkedIn",
            type: "string"
        },

        // ── Lead / Sales ─────────────────────────────────────────
        // Moved to the `company-leads` collection. `public_read` below is
        // `USING (true)` so the job board can render companies for signed-out
        // visitors, and RLS cannot exclude columns — so anything on this row is
        // effectively public. requested_services, needs_description and
        // lead_status were served to anonymous callers for exactly that reason.
        // Nothing commercially sensitive belongs on this table.

        // ── Meta ─────────────────────────────────────────────────
        /**
         * Where this row came from, stamped once at creation.
         *
         * Of 153 live companies only 4 have an account: the rest were created
         * automatically so job cards would have a logo, and the list read as
         * 153 clients. Provenance was derivable — every automatic row has a
         * `domain` and a `brand_fetched_at` and no hand-made one does — but
         * only by accident: `job-company-linker` enriches form-posted employers
         * by name too, so a registered customer picks up both fields. Hence a
         * stored value, written at creation and never on update.
         *
         * `scraping` and `submission` are separate because both run through the
         * same enrichment insert and mean opposite things — only 12 of the 148
         * automatic rows came from a scraped offer; 80 came from vacancies
         * people posted through the form. `enrichment` is the honest label for
         * the 56 older rows whose calling pipeline was never recorded.
         *
         * Ids are English to match `jobs.source`; the Spanish is in the labels.
         */
        source: {
            name: "Origen",
            type: "string",
            enum: [
                { id: "scraping", label: "La encontramos en otra web", color: "blue" },
                { id: "submission", label: "Envió una vacante", color: "cyan" },
                // "Automática" said which branch of the enum this was and
                // nothing about the company. What actually happened is that
                // nobody chose to create it: a vacancy needed a logo and the
                // system made a record to hang it on.
                { id: "enrichment", label: "La creó el sistema", color: "grayLighter" },
                { id: "signup", label: "Se registró en la web", color: "green" },
                { id: "manual", label: "La creamos a mano", color: "gray" }
            ],
            defaultValue: "manual",
            description: "Cómo entró esta empresa: por una oferta scrapeada, por una vacante enviada desde el formulario, automática (creada por el enriquecimiento antes de que se registrara el origen), por registro de la propia empresa, o a mano."
        },
        is_claimed: {
            name: "Reclamada",
            type: "boolean",
            description: "Si al menos una persona ha reclamado esta empresa",
            defaultValue: false
        },
        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        },
        updated_at: {
            name: "Actualizado",
            type: "date",
            autoValue: "on_update"
        },

        // ── Relations ────────────────────────────────────────────
        //
        // Each of these is a tab on the company, built by `getEntityChildViews`
        // from the relation itself. Declaring it here is what *makes* the tab —
        // but the form then renders the same relation a second time as an inline
        // picker, which is a dropdown you cannot pick a company's children from
        // and which shows a red "Unexpected value" box per row.
        //
        // `HIDDEN_FROM_FORM` suppresses only the form field; the tab is derived
        // from the relation, not the field, so it survives. See talents.ts for
        // the same treatment.
        jobs: {
            name: "Vacantes",
            type: "relation",
            relation: {
                kind: "hasMany",
                target: () => jobsCollection,
                foreignKeyOnTarget: "company_id"
            },
            conditions: HIDDEN_FROM_FORM
        },
        // Five more `hasMany` relations were declared here — scraped jobs,
        // members, leads, applications received, profiles viewed — and every
        // one became a tab, which made an employer's record nine tabs wide
        // (2026-09-06). Nothing read them as relations: the site, the backend
        // and the panel all query the child tables directly. Two of them are
        // now sections of the cover (`views/CompanyOverview.tsx`), where the
        // people and the commercial record belong; the rest are reachable
        // from their own collections. A record has four tabs: Resumen, the
        // form, Correo, Vacantes.
    },
    admin: {
        group: "Empresas",
        /**
         * Full screen, not a side panel.
         *
         * On 2026-09-06 a vacancy opened in a strip about 180 px wide beside
         * the table — the title wrapped four times and the cover did not fit —
         * and nothing said the panel could be widened. A record's cover is a
         * page: "Lo siguiente", the profile, the correspondence. The queue
         * views keep their own side panel over the queue (see
         * `collectionViews` in frontend/src/App.tsx); this is the list.
         */
        openEntityMode: "full_screen",
        /**
         * An employer opens on its cover, not on its form.
         *
         * `resumen` is registered in `frontend/src/App.tsx` and drawn by
         * `views/CompanyOverview.tsx`: their vacancies, where the outreach
         * stands under their `employer_key`, and the correspondence — the three
         * questions this record gets opened to answer, two of which lived in
         * other tables. Half this form is brand data the enrichment writes and
         * nobody reads field by field.
         *
         * A builder rather than the bare key, as on `talents` and `jobs`: a
         * company being created has no id, so the cover would summarise nothing.
         */
        defaultSelectedView: ({ status }) => (status === "new" ? undefined : "resumen"),
        icon: "Building2",
        sort: ["created_at", "desc"],
        // `industry`, `size`, `website` and `linkedin_url` are omitted on
        // purpose — every one is null across all 153 live rows, so they would
        // be four empty columns.
        // `domain` is absent: `display.subtitle` already prints it under the
        // name, and listing it again put the same string twice on every row.
        listProperties: ["name", "source", "is_claimed", "created_at"],
        // 131 of 163 companies have a logo the enrichment already fetched, and
        // none of them showed it anywhere — every row and every relation chip
        // rendered the same grey building icon.
        //
        // Declaring it here does two things: this list gets the logos, and so
        // does every surface that *references* a company. An included relation
        // is projected down to the target's display and preview keys, so a
        // vacancy could not reach `logo_url` through `company_id` until it was
        // one of them — which is why the jobs list fell back to its icon even
        // with the whole company row apparently in hand.
        display: {
            title: "name",
            subtitle: "domain",
            image: "logo_url",
            status: "source",
            date: "created_at"
        },
        previewProperties: ["name", "domain"],
        // Open on the clients. 196 rows, of which 9 are companies anybody has a
        // relationship with; the rest exist to put a logo on a vacancy, and a
        // list that mixes the two reads as 196 clients. The preset below
        // matches this exactly, so it shows as active, and one click clears it.
        defaultFilter: { source: ["in", ["signup", "manual"]] },
        // "Empresas · 153" reads as 153 clients; four of them are. These are
        // the two questions actually asked of this table.
        filterPresets: [
            {
                label: "Clientes",
                filterValues: { source: ["in", ["signup", "manual"]] }
            },
            {
                label: "Las encontramos en otras webs",
                filterValues: { source: ["==", "scraping"] }
            },
            {
                label: "Creadas por el sistema",
                filterValues: { source: ["in", ["submission", "enrichment"]] }
            }
        ],
        // Every brand field is written by the Unbrand enrichment and overwritten
        // on the next run — they are evidence, not inputs, and are already
        // readOnly. Collapsing them keeps the editable identity fields at the
        // top instead of buried under a palette and a raw API payload.
        form: {
            sidebar: ["is_claimed", "domain", "brand_fetched_at", "popularity_score"],
            sections: [
                {
                    key: "identity",
                    properties: ["name", "slug", "tagline", "description", "mission"]
                },
                {
                    key: "profile",
                    title: "Perfil",
                    properties: ["industry", "size", "location", "city", "country", "website", "linkedin_url"]
                },
                {
                    key: "brand",
                    title: "Marca (el logo y los colores que encontró el sistema)",
                    collapsed: true,
                    properties: [
                        "logo_url",
                        "logo_svg",
                        "primary_color",
                        "background_color",
                        "text_color",
                        "colors",
                        "fonts",
                        "raw_brand_data"
                    ]
                }
            ]
        }
    }
};

export default companiesCollection;
